/**
 * SIM-SZENE LEIPZIG – ScenarioPanel
 * Zeigt ein Szenario mit Hintergrundbild und Optionen.
 */
import { useRef, useState } from 'preact/hooks';
import { balancing } from '../data/loader.js';
import { shareScenarioCard } from '../utils/share.js';
import { optionUsesDice } from '../utils/dice.js';
import './ScenarioPanel.css';

export function ScenarioPanel({ scenario, onChoice }) {
  const panelRef = useRef(null);
  const [shareStatus, setShareStatus] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);

  if (!scenario) {
    return <div className="scenario-panel loading">Lade...</div>;
  }
  
  const categoryColor = balancing.categories[scenario.category]?.color ?? '#4a90d9';
  const categoryLabel = balancing.categories[scenario.category]?.label ?? scenario.category;
  const backgroundUrl = `/assets/backgrounds/bg-${scenario.category}.png`;

  const handleShare = async () => {
    if (!panelRef.current || shareBusy) return;
    setShareBusy(true);
    setShareStatus(null);
    try {
      const result = await shareScenarioCard({
        element: panelRef.current,
        title: scenario.title,
        categoryLabel,
      });
      setShareStatus(result);
      if (result !== 'unsupported') setTimeout(() => setShareStatus(null), 3000);
    } catch (e) {
      setShareStatus('unsupported');
      setTimeout(() => setShareStatus(null), 3000);
    } finally {
      setShareBusy(false);
    }
  };

  const shareFeedback = {
    shared: 'Geteilt!',
    downloaded: 'Bild gespeichert',
    copied: 'Text kopiert',
    unsupported: 'Teilen nicht möglich',
  };

  return (
    <div className="scenario-panel" ref={panelRef}>
      {/* Hintergrund: Kategorie-Bild (Fallback: Kategorie-Farbe) */}
      <div 
        className="scenario-background"
        style={{ 
          '--category-color': categoryColor,
          backgroundImage: `url(${backgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top center'
        }}
      >
        <div className="scenario-bg-overlay"></div>
      </div>
      
      {/* Content-Box (Dialog im American-Dream-Stil) */}
      <div className="scenario-content">
        {/* Kategorie-Badge */}
        <span 
          className="scenario-category"
          style={{ backgroundColor: categoryColor }}
        >
          {categoryLabel}
        </span>
        
        {/* Titel */}
        <h2 className="scenario-title">{scenario.title}</h2>
        
        {/* Beschreibung */}
        <div className="scenario-body">
          {scenario.body.split('\n').map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
        
        {/* Kontext (wenn vorhanden) */}
        {scenario.context && (
          <div className="scenario-context">
            {scenario.context.split('\n').map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        )}
      </div>
      
      {/* Optionen (Buttons) */}
      <div className="scenario-options">
        {scenario.options.map((option, index) => (
          <button
            key={index}
            className="option-button"
            onClick={() => onChoice(option)}
          >
            <span className="option-label">{option.label}</span>
            {optionUsesDice(option) ? (
              <span className="effect-preview effect-preview--dice" title="Ergebnis zufällig (Würfel)">
                🎲 Zufall
              </span>
            ) : (
              <EffectPreview effects={option.effects} />
            )}
          </button>
        ))}
        <div className="scenario-share-row">
          <button
            type="button"
            className="scenario-share-button"
            onClick={handleShare}
            disabled={shareBusy}
            title="Szenario als Bild teilen"
          >
            {shareBusy ? 'Wird erstellt…' : '📤 Szenario teilen'}
          </button>
          {shareStatus && (
            <span className="scenario-share-feedback" role="status">
              {shareFeedback[shareStatus]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Zeigt eine Vorschau der Effekte (Pfeile hoch/runter)
 */
function EffectPreview({ effects }) {
  if (!effects) return null;
  
  const indicators = [];
  
  if (effects.geld && effects.geld !== 0) {
    indicators.push(
      <span key="geld" className={`effect-indicator ${effects.geld > 0 ? 'positive' : 'negative'}`}>
        💰{effects.geld > 0 ? '↑' : '↓'}
      </span>
    );
  }
  
  if (effects.nerven && effects.nerven !== 0) {
    indicators.push(
      <span key="nerven" className={`effect-indicator ${effects.nerven > 0 ? 'positive' : 'negative'}`}>
        🧠{effects.nerven > 0 ? '↑' : '↓'}
      </span>
    );
  }
  
  if (effects.credibility && effects.credibility !== 0) {
    indicators.push(
      <span key="credibility" className={`effect-indicator ${effects.credibility > 0 ? 'positive' : 'negative'}`}>
        😎{effects.credibility > 0 ? '↑' : '↓'}
      </span>
    );
  }
  
  if (indicators.length === 0) return null;
  
  return <span className="effect-preview">{indicators}</span>;
}
